/**
 * BackupsTabContent Component
 *
 * Orchestrator for the Backups tab inside TenantDetailsModal.
 * Shows policy status, effective config, override controls,
 * Backup Now button, and backup history.
 *
 * Phase 3.7.4 + WQ-036/WQ-037: Tier-Based Backup Policy
 * @module components/backups/BackupsTabContent
 */

import { useState, useEffect, useCallback } from 'react';
import { BackupHistoryList } from './BackupHistoryList';
import { RestoreDialog } from './RestoreDialog';
import type {
  BackupHistoryItem,
  BackupHistoryListResponse,
} from '../../types/backup';
import type { EffectiveConfig } from '../../services/backupPolicyService';
import * as backupService from '../../services/backupService';
import * as policyService from '../../services/backupPolicyService';
import {
  ShieldIcon,
  CheckCircleIcon,
  ClockIcon,
  CloudIcon,
  DatabaseIcon,
  RefreshIcon,
  AlertIcon,
} from '../common/Icons';

/** Convert cron expression or keyword to human-readable label */
function formatFrequency(freq: string): string {
  const CRON_LABELS: Record<string, string> = {
    'hourly': 'Hourly',
    'daily': 'Daily',
    'weekly': 'Weekly',
    '0 */6 * * *': 'Every 6 hours',
    '0 2 * * *': 'Daily (2 AM)',
    '0 3 * * *': 'Daily (3 AM)',
    '0 2 * * 0': 'Weekly (Sun 2 AM)',
    '0 0 * * *': 'Daily (midnight)',
    '0 */12 * * *': 'Every 12 hours',
  };
  return CRON_LABELS[freq] || freq;
}

type AuthFetch = (url: string, options?: RequestInit) => Promise<Response>;

interface BackupsTabContentProps {
  tenantId: string;
  authFetch: AuthFetch;
  isSuperAdmin: boolean;
  /** WQ-253 H-002: when true, render an explanatory note instead of
   * the per-tenant backup view. System tenants (Default Org) are
   * covered by the platform-level backup, not per-tenant backups. */
  isSystemTenant?: boolean;
}

const PAGE_SIZE = 10;

const TIER_LABELS: Record<string, string> = {
  starter: 'Starter',
  professional: 'Professional',
  enterprise: 'Enterprise',
};

const DEST_LABELS: Record<string, string> = {
  local: 'Local filesystem',
  s3: 'Amazon S3',
  azure: 'Azure Blob Storage',
  gcs: 'Google Cloud Storage',
  external: 'External mount',
};

const sectionStyle: React.CSSProperties = {
  background: 'rgba(255, 255, 255, 0.03)',
  borderRadius: '12px',
  padding: '16px',
  border: '1px solid rgba(255, 255, 255, 0.08)',
};

const labelStyle: React.CSSProperties = {
  display: 'block', fontSize: '12px', color: 'var(--text-secondary)',
  marginBottom: '4px', fontWeight: 500,
};

const selectStyle: React.CSSProperties = {
  padding: '6px 10px',
  background: 'rgba(0, 0, 0, 0.2)',
  border: '1px solid rgba(255, 255, 255, 0.1)',
  borderRadius: '6px',
  color: 'var(--text-primary)',
  fontSize: '13px',
  cursor: 'pointer',
  minWidth: '180px',
};

const inputStyle: React.CSSProperties = {
  padding: '6px 10px',
  background: 'rgba(0, 0, 0, 0.2)',
  border: '1px solid rgba(255, 255, 255, 0.1)',
  borderRadius: '6px',
  color: 'var(--text-primary)',
  fontSize: '13px',
  width: '100%',
};

export const BackupsTabContent: React.FC<BackupsTabContentProps> = ({
  tenantId,
  authFetch,
  isSuperAdmin,
  isSystemTenant = false,
}) => {
  // WQ-253 H-002: short-circuit for system tenants. Default Organization
  // (`is_system=true`) is covered by platform-level backup, not per-tenant.
  // Showing the per-tenant policy/history surface is a UX trap — operator
  // sees stale "no per-tenant config" / "last backup: 38 days ago" and
  // reasonably assumes something is broken. Filed as POLISH_BACKLOG H-002
  // during WQ-252 ship; resolved here.
  if (isSystemTenant) {
    return (
      <div
        data-testid="backups-system-tenant-note"
        style={{
          padding: '24px',
          background: 'rgba(59, 130, 246, 0.08)',
          border: '1px solid rgba(59, 130, 246, 0.25)',
          borderRadius: 8,
          margin: '12px 0',
        }}
      >
        <div style={{ display: 'flex', alignItems: 'flex-start', gap: 12 }}>
          <ShieldIcon size={24} style={{ color: 'var(--accent-primary, #3b82f6)', flexShrink: 0 }} />
          <div>
            <h3 style={{ margin: 0, fontSize: 16, fontWeight: 600, color: 'var(--text-primary)' }}>
              System tenant — covered by platform-level backup
            </h3>
            <p style={{ marginTop: 8, marginBottom: 0, fontSize: 13, color: 'var(--text-secondary)', lineHeight: 1.6 }}>
              The Default Organization is required platform infrastructure
              that lives in the <code>platform_core</code> schema. Its data
              is included in the <strong>platform-level backup</strong> (see
              <em> Operations &rarr; Platform Backups</em>) — not the per-tenant
              backup chain. No per-tenant configuration is required.
            </p>
            <p style={{ marginTop: 8, marginBottom: 0, fontSize: 12, color: 'var(--text-tertiary)' }}>
              Any orphan history rows for system tenants are residual from
              prior misconfiguration and are harmless.
            </p>
          </div>
        </div>
      </div>
    );
  }

  const [effective, setEffective] = useState<EffectiveConfig | null>(null);
  const [effectiveLoading, setEffectiveLoading] = useState(true);
  const [effectiveError, setEffectiveError] = useState<string | null>(null);
  const [historyData, setHistoryData] = useState<BackupHistoryListResponse>({
    items: [], total: 0, limit: PAGE_SIZE, offset: 0,
  });
  const [historyLoading, setHistoryLoading] = useState(true);
  const [historyError, setHistoryError] = useState<string | null>(null);
  const [restoreTarget, setRestoreTarget] = useState<BackupHistoryItem | null>(null);
  const [verifyMsg, setVerifyMsg] = useState<string | null>(null);
  const [typeFilter, setTypeFilter] = useState('');
  const [tierFilter, setTierFilter] = useState('');

  // Override UI state
  const [overrideSelection, setOverrideSelection] = useState<string>('inherit');
  const [saving, setSaving] = useState(false);
  const [successMsg, setSuccessMsg] = useState<string | null>(null);
  const [errorMsg, setErrorMsg] = useState<string | null>(null);

  // Custom override form fields
  const [customFreq, setCustomFreq] = useState('daily');
  const [customRetCount, setCustomRetCount] = useState(7);
  const [customRetDays, setCustomRetDays] = useState(30);
  const [customWeeklyRet, setCustomWeeklyRet] = useState(4);
  const [customMonthlyRet, setCustomMonthlyRet] = useState(3);
  const [customEnabled, setCustomEnabled] = useState(true);

  const loadEffective = useCallback(async () => {
    setEffectiveLoading(true);
    setEffectiveError(null);
    try {
      const data = await policyService.getEffectiveConfig(authFetch, tenantId);
      setEffective(data);
      // Sync override selector with current state
      if (data.source === 'override') {
        setOverrideSelection('custom');
        setCustomFreq(data.frequency);
        setCustomRetCount(data.retention_count);
        setCustomRetDays(data.retention_days);
        setCustomWeeklyRet(data.weekly_retention_count ?? 4);
        setCustomMonthlyRet(data.monthly_retention_count ?? 3);
        setCustomEnabled(data.enabled);
      } else {
        setOverrideSelection('inherit');
      }
    } catch (err: unknown) {
      setEffective(null);
      setEffectiveError(err instanceof Error ? err.message : 'Failed to load policy');
    } finally {
      setEffectiveLoading(false);
    }
  }, [authFetch, tenantId]);

  const loadHistory = useCallback(
    async (offset = 0) => {
      setHistoryLoading(true);
      setHistoryError(null);
      try {
        const filters: { backup_type?: string; retention_tier?: string } = {};
        if (typeFilter) filters.backup_type = typeFilter;
        if (tierFilter) filters.retention_tier = tierFilter;
        const data = await backupService.listBackupHistory(authFetch, tenantId, PAGE_SIZE, offset, filters);
        setHistoryData(data);
      } catch (err) {
        setHistoryError(err instanceof Error ? err.message : 'Failed to load backup history');
      } finally {
        setHistoryLoading(false);
      }
    },
    [authFetch, tenantId, typeFilter, tierFilter],
  );

  useEffect(() => {
    loadEffective();
    loadHistory();
  }, [loadEffective, loadHistory]);

  const handleVerify = async (backupId: string) => {
    try {
      const result = await backupService.verifyBackup(authFetch, tenantId, backupId);
      setVerifyMsg(result.message);
      setTimeout(() => setVerifyMsg(null), 4000);
    } catch (err: unknown) {
      setVerifyMsg(err instanceof Error ? err.message : 'Verification failed');
      setTimeout(() => setVerifyMsg(null), 4000);
    }
  };

  const handleDelete = async (backupId: string) => {
    if (!window.confirm('Delete this backup? This action cannot be undone.')) return;
    try {
      await backupService.deleteBackup(authFetch, tenantId, backupId);
      loadHistory(historyData.offset);
    } catch {
      // Silently fail
    }
  };

  const handleOverrideChange = async (value: string) => {
    setOverrideSelection(value);
    setErrorMsg(null);

    if (value === 'custom') {
      // Just switch to custom editing mode — don't save yet
      return;
    }

    setSaving(true);
    try {
      if (value === 'inherit') {
        // Remove override, revert to tier policy
        await policyService.removeTenantOverride(authFetch, tenantId);
        setSuccessMsg('Reverted to tier policy');
      } else {
        // Link to a specific tier
        await policyService.setTenantOverride(authFetch, tenantId, { policy_tier: value });
        setSuccessMsg(`Linked to ${TIER_LABELS[value] || value} policy`);
      }
      setTimeout(() => setSuccessMsg(null), 3000);
      await loadEffective();
    } catch (err: unknown) {
      setErrorMsg(err instanceof Error ? err.message : 'Failed to update override');
      // Revert selector on error
      if (effective?.source === 'override') {
        setOverrideSelection('custom');
      } else {
        setOverrideSelection('inherit');
      }
    } finally {
      setSaving(false);
    }
  };

  const handleSaveCustom = async () => {
    setSaving(true);
    setErrorMsg(null);
    try {
      await policyService.setTenantOverride(authFetch, tenantId, {
        frequency: customFreq,
        retention_count: customRetCount,
        retention_days: customRetDays,
        weekly_retention_count: customWeeklyRet,
        monthly_retention_count: customMonthlyRet,
        enabled: customEnabled,
      });
      setSuccessMsg('Custom override saved');
      setTimeout(() => setSuccessMsg(null), 3000);
      await loadEffective();
    } catch (err: unknown) {
      setErrorMsg(err instanceof Error ? err.message : 'Failed to save custom override');
    } finally {
      setSaving(false);
    }
  };

  // Derived display values
  const sourceLabel = effective?.source === 'override' ? 'Custom Override'
    : effective?.source === 'policy' ? `${TIER_LABELS[effective.policy_tier || ''] || effective.policy_tier} Policy`
    : 'Default';

  const sourceBadgeColor = effective?.source === 'override' ? 'rgba(234, 179, 8, 0.2)'
    : effective?.source === 'policy' ? 'rgba(124, 58, 237, 0.2)'
    : 'rgba(255, 255, 255, 0.1)';

  const sourceBadgeTextColor = effective?.source === 'override' ? '#eab308'
    : effective?.source === 'policy' ? '#a78bfa'
    : 'var(--text-secondary)';

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
      {/* Messages */}
      {successMsg && (
        <div className="alert alert-success" style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
          <CheckCircleIcon size={14} /> {successMsg}
        </div>
      )}
      {errorMsg && (
        <div className="alert alert-error">{errorMsg}</div>
      )}
      {verifyMsg && (
        <div className="alert alert-success" style={{ fontSize: '13px', display: 'flex', alignItems: 'center', gap: '6px' }}>
          {verifyMsg}
        </div>
      )}

      {/* Policy Status Section */}
      {effectiveLoading ? (
        <div style={{ textAlign: 'center', padding: '20px', color: 'var(--text-secondary)' }}>
          <RefreshIcon size={16} className="spin" /> Loading backup policy...
        </div>
      ) : effectiveError ? (
        <div style={sectionStyle}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px', color: 'var(--text-secondary)', fontSize: '13px' }}>
            <AlertIcon size={16} style={{ color: 'var(--warning, #eab308)' }} />
            <span>Could not load backup policy: {effectiveError}</span>
          </div>
        </div>
      ) : effective ? (
        <div style={sectionStyle}>
          {/* Header with badge */}
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '14px' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
              <ShieldIcon size={16} style={{ color: 'var(--accent-primary, #7c3aed)' }} />
              <span style={{ fontWeight: 600, fontSize: '14px' }}>Backup Policy</span>
              <span style={{
                padding: '2px 10px',
                borderRadius: '12px',
                background: sourceBadgeColor,
                color: sourceBadgeTextColor,
                fontSize: '12px',
                fontWeight: 500,
              }}>
                {sourceLabel}
              </span>
            </div>
          </div>

          {/* Effective Settings Grid (always visible) */}
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '12px', fontSize: '13px', marginBottom: '14px' }}>
            <div>
              <span style={labelStyle}><ClockIcon size={12} /> Frequency</span>
              <div style={{ color: 'var(--text-primary)' }}>{formatFrequency(effective.frequency)}</div>
            </div>
            <div>
              <span style={labelStyle}><DatabaseIcon size={12} /> Retention</span>
              <div style={{ color: 'var(--text-primary)', fontSize: '13px' }}>
                Daily: {effective.retention_count} &middot; Weekly: {effective.weekly_retention_count ?? '\u2014'} &middot; Monthly: {effective.monthly_retention_count ?? '\u2014'}
              </div>
              <div style={{ color: 'var(--text-secondary)', fontSize: '11px', marginTop: '2px' }}>
                Expires after {effective.retention_days} days
              </div>
            </div>
            <div>
              <span style={labelStyle}><CloudIcon size={12} /> Destination</span>
              <div style={{ color: 'var(--text-primary)' }}>{DEST_LABELS[effective.destination_type] || effective.destination_type}</div>
            </div>
          </div>

          {/* Status row */}
          <div style={{ display: 'flex', gap: '16px', alignItems: 'center', fontSize: '13px', marginBottom: '14px' }}>
            <div>
              <span style={labelStyle}>Status</span>
              <span style={{
                padding: '2px 8px',
                borderRadius: '8px',
                fontSize: '12px',
                fontWeight: 500,
                background: effective.enabled ? 'rgba(34, 197, 94, 0.15)' : 'rgba(107, 114, 128, 0.15)',
                color: effective.enabled ? '#22c55e' : '#6b7280',
              }}>
                {effective.enabled ? 'Enabled' : 'Disabled'}
              </span>
            </div>
            {effective.policy_tier && effective.source === 'policy' && (
              <div>
                <span style={labelStyle}>Tier</span>
                <span style={{ color: 'var(--text-primary)' }}>{TIER_LABELS[effective.policy_tier] || effective.policy_tier}</span>
              </div>
            )}
          </div>

          {/* Override Selector — super_admin only */}
          {isSuperAdmin && (
          <div style={{
            borderTop: '1px solid rgba(255, 255, 255, 0.06)',
            paddingTop: '12px',
            display: 'flex',
            alignItems: 'center',
            gap: '10px',
            flexWrap: 'wrap',
          }}>
            <span style={{ fontSize: '13px', color: 'var(--text-secondary)', fontWeight: 500 }}>Override:</span>
            <select
              style={selectStyle}
              value={overrideSelection}
              onChange={(e) => handleOverrideChange(e.target.value)}
              disabled={saving}
            >
              <option value="inherit">Inherit from tier</option>
              <option value="starter">Starter</option>
              <option value="professional">Professional</option>
              <option value="enterprise">Enterprise</option>
              <option value="custom">Custom</option>
            </select>
            {saving && <RefreshIcon size={14} className="spin" />}
          </div>
          )}

          {/* Custom override form — super_admin only */}
          {isSuperAdmin && overrideSelection === 'custom' && (
            <div style={{
              marginTop: '12px',
              padding: '12px',
              background: 'rgba(0, 0, 0, 0.15)',
              borderRadius: '8px',
              border: '1px solid rgba(255, 255, 255, 0.06)',
            }}>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr 1fr 1fr', gap: '12px', marginBottom: '12px' }}>
                <div>
                  <span style={labelStyle}>Frequency</span>
                  <select style={{ ...selectStyle, width: '100%', minWidth: 0 }} value={customFreq} onChange={(e) => setCustomFreq(e.target.value)}>
                    <option value="hourly">Hourly</option>
                    <option value="0 */6 * * *">Every 6 hours</option>
                    <option value="0 2 * * *">Daily (2 AM)</option>
                    <option value="daily">Daily</option>
                    <option value="0 2 * * 0">Weekly (Sun 2 AM)</option>
                    <option value="weekly">Weekly</option>
                  </select>
                </div>
                <div>
                  <span style={labelStyle}>Daily (count)</span>
                  <input type="number" style={inputStyle} value={customRetCount} min={1} max={365}
                    onChange={(e) => setCustomRetCount(parseInt(e.target.value) || 1)} />
                </div>
                <div>
                  <span style={labelStyle}>Weekly (count)</span>
                  <input type="number" style={inputStyle} value={customWeeklyRet} min={1} max={365}
                    onChange={(e) => setCustomWeeklyRet(parseInt(e.target.value) || 1)} />
                </div>
                <div>
                  <span style={labelStyle}>Monthly (count)</span>
                  <input type="number" style={inputStyle} value={customMonthlyRet} min={1} max={365}
                    onChange={(e) => setCustomMonthlyRet(parseInt(e.target.value) || 1)} />
                </div>
                <div>
                  <span style={labelStyle}>Expires (days)</span>
                  <input type="number" style={inputStyle} value={customRetDays} min={1} max={3650}
                    onChange={(e) => setCustomRetDays(parseInt(e.target.value) || 1)} />
                </div>
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '12px' }}>
                <label style={{ display: 'flex', alignItems: 'center', gap: '6px', fontSize: '13px', cursor: 'pointer' }}>
                  <input type="checkbox" checked={customEnabled} onChange={(e) => setCustomEnabled(e.target.checked)} />
                  Enabled
                </label>
              </div>
              <div style={{ display: 'flex', gap: '8px' }}>
                <button className="btn btn-sm btn-primary" onClick={handleSaveCustom} disabled={saving}>
                  {saving ? <><RefreshIcon size={14} className="spin" /> Saving...</> : 'Save Custom Override'}
                </button>
                <button className="btn btn-sm btn-secondary" onClick={() => handleOverrideChange('inherit')} disabled={saving}>
                  Revert to Tier
                </button>
              </div>
            </div>
          )}

          {/* Tenant admin info box */}
          {!isSuperAdmin && (
            <div style={{
              marginTop: '12px',
              padding: '10px 14px',
              background: 'rgba(59, 130, 246, 0.08)',
              borderRadius: '8px',
              border: '1px solid rgba(59, 130, 246, 0.2)',
              fontSize: '12px',
              color: 'var(--text-secondary)',
            }}>
              Backup schedule is managed by your platform administrator.
              Backups run automatically per your tier policy. You can restore from any completed backup below.
            </div>
          )}
        </div>
      ) : null}

      {/* Backup Now button — super_admin only */}
      {isSuperAdmin && (
      <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
        <BackupNowButton tenantId={tenantId} authFetch={authFetch} onTriggered={() => {
          // Reload history twice: once quickly to show the pending record, again after 5s for completion
          setTimeout(() => loadHistory(0), 1000);
          setTimeout(() => loadHistory(0), 5000);
        }} />
      </div>
      )}

      {/* History */}
      <div style={{ borderTop: '1px solid rgba(255, 255, 255, 0.06)', paddingTop: '16px' }}>
        {historyError && (
          <div style={{ padding: '12px', borderRadius: '8px', background: 'rgba(239, 68, 68, 0.15)', border: '1px solid rgba(239, 68, 68, 0.3)', fontSize: '13px', color: 'var(--error)', marginBottom: '12px', display: 'flex', alignItems: 'center', gap: '8px' }}>
            <AlertIcon size={16} />
            <span>Failed to load backup history: {historyError}</span>
            <button className="btn btn-ghost btn-sm" onClick={() => loadHistory(0)} style={{ marginLeft: 'auto', fontSize: '12px' }}>Retry</button>
          </div>
        )}
        <BackupHistoryList
          items={historyData.items}
          total={historyData.total}
          limit={historyData.limit}
          offset={historyData.offset}
          loading={historyLoading}
          onPageChange={loadHistory}
          onRestore={setRestoreTarget}
          onVerify={handleVerify}
          onDelete={handleDelete}
          onRefresh={() => loadHistory(historyData.offset)}
          isSuperAdmin={isSuperAdmin}
          typeFilter={typeFilter}
          tierFilter={tierFilter}
          onTypeFilterChange={(v) => { setTypeFilter(v); }}
          onTierFilterChange={(v) => { setTierFilter(v); }}
        />
      </div>

      {restoreTarget && (
        <RestoreDialog
          isOpen={!!restoreTarget}
          backup={restoreTarget}
          tenantId={tenantId}
          authFetch={authFetch}
          onClose={() => setRestoreTarget(null)}
          onRestoreComplete={() => {
            setRestoreTarget(null);
            loadHistory(0);
          }}
        />
      )}
    </div>
  );
};

// Small helper component for Backup Now
const BackupNowButton: React.FC<{
  tenantId: string;
  authFetch: AuthFetch;
  onTriggered: () => void;
}> = ({ tenantId, authFetch, onTriggered }) => {
  const [triggering, setTriggering] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);

  const handleBackupNow = async () => {
    setTriggering(true);
    setError(null);
    setSuccess(false);
    try {
      await backupService.triggerBackup(authFetch, tenantId);
      setSuccess(true);
      onTriggered();
      // Clear success after 5s
      setTimeout(() => setSuccess(false), 5000);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to trigger backup');
    } finally {
      setTriggering(false);
    }
  };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: '4px' }}>
      <button className="btn btn-secondary btn-sm" onClick={handleBackupNow} disabled={triggering}>
        {triggering ? <><RefreshIcon size={14} className="spin" /> Running...</> : 'Backup Now'}
      </button>
      {error && (
        <div style={{ fontSize: '12px', color: 'var(--error)', maxWidth: '300px', textAlign: 'right' }}>
          {error}
        </div>
      )}
      {success && (
        <div style={{ fontSize: '12px', color: 'var(--success)' }}>
          Backup triggered — refreshing history...
        </div>
      )}
    </div>
  );
};
